import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useLocation } from 'react-router'
import { useCallback, useEffect, useRef } from 'react'
import httpClient from '../httpClient'
import Modal from '../components/common/Modals/BaseModal'
import useProductSelectionStore from '../stores/useProductSelectionStore'
import useAlbumSelectionStore from '../stores/useAlbumSelectionStore'
import useMultiOpeningMatStore from '../stores/useMultiOpeningMatStore'
import { ProductSelectionSidebar, ProductTypeSelector } from '../components/ProductSelection'
import { PhotoDisplay, SuccessModal } from '../components/ProductSelection/common'
import { AlbumGrid, AlbumStatusBar } from '../components/ProductSelection/Albums'
import ImagesUpload from '../components/PrintOrder/PrintOrderCreate/ImagesUpload'
import Loading from '../components/common/Loading'
import styles from './ProductSelection.module.css'
import { COMING_SOON_ALBUM_TYPES, MUSEUM_CASE_ORDER_HERE_URL, DOUBLE_FOLIO_ORDER_HERE_URL } from '../lib/const'
import {
  filterPhotosByProductType,
} from '../lib/utils'

const ProductSelection = () => {
  const { id, lineItemId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Get state and actions from store
  const {
    ModalIsOpen,
    setModalIsOpen,
    photographGuid,
    form,
    photos,
    lineItem,
    productType,
    photosLoadingMessage,
    setPhotos,
    setSelectedPhoto,
    setPhotosLoadingMessage,
    setCartCount,
    setPhotographGuid,
    setSubtotal,
    setLineItem,
    setNotes,
    processPaperTypeData,
    resetEditMode,
    setIsEditInitializing,
    setProductType,
    handleUploadPhoto,
    addToCart,
    uploadsPreview,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    successModalMessage,
    lineItemProductTypes,
  } = useProductSelectionStore()

  const {
    resetAlbumForm,
    albumItems,
    setAlbumItems,
    albumForm,
    hydrateFromLineItem: hydrateAlbumFromLineItem,
  } = useAlbumSelectionStore()

  const reviewOrderModalMessage =
    productType === 'albums'
      ? albumForm?.meta?.reviewOrderError
      : form?.meta?.reviewOrderError
  const selectedAlbumType = albumForm?.inputs?.albumType?.selectedValue
  const isComingSoonAlbum = COMING_SOON_ALBUM_TYPES.includes(selectedAlbumType)
  const quantityRef = useRef(null)

  // Reset album items
  useEffect(() => {
    if (productType === 'albums') {
      setAlbumItems(photos)
    } else {
      setAlbumItems([])
    }
  }, [productType, photos, setAlbumItems]);

  // Fetch photos query
  const photosQuery = useQuery({
    queryKey: ['photos', id],
    queryFn: () => httpClient.get(`lab_orders/${id}/lab_order_photographs`),
    enabled: !!id && !lineItemId && productType !== 'default',
    staleTime: Infinity,
    refetchOnMount: 'always',
    onError: () => {
      setPhotosLoadingMessage(false)
    },
  })

  useEffect(() => {
    if (lineItemId) return
  
    const { isError, isSuccess, data } = photosQuery
  
    if (isError) {
      setPhotosLoadingMessage(false)
      return
    }
  
    if (!isSuccess || !data?.data) return
  
    setPhotosLoadingMessage(false)
  
    const filtered = filterPhotosByProductType(data.data, productType )
    setPhotos(filtered)

    const sortedPhotos = useProductSelectionStore.getState().photos

    if (!sortedPhotos.length) {
      setSelectedPhoto('')
      return
    }

    const forceFirstPhoto = !!location.state?.forceSelectFirstPhoto

    const prevGuid =
      useProductSelectionStore.getState().selectedPhoto?.attributes?.guid

    const nextSelected = forceFirstPhoto
      ? sortedPhotos[0]
      : sortedPhotos.find((p) => p.attributes?.guid === prevGuid) || sortedPhotos[0]

    setSelectedPhoto(nextSelected)

    if (forceFirstPhoto) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [
    lineItemId,
    photosQuery.isSuccess,
    photosQuery.isError,
    photosQuery.data,
    productType,
  ])

  // Fetch order photo query (for editing line items)
  const orderPhotoQuery = useQuery({
    queryKey: ['orderPhoto', id, photographGuid],
    queryFn: () => httpClient.get(`lab_orders/${id}/photographs/${photographGuid}`),
    enabled: !!id && !!photographGuid && !!lineItemId,
    staleTime: Infinity,
    refetchOnMount: 'always',
    onSuccess: ({ data }) => {
      setPhotos(data)
      setSelectedPhoto(data[0])
      setPhotosLoadingMessage(false)
    },
    onError: () => {
      setPhotosLoadingMessage(false)
    },
  })

  const labOrderQuery = useQuery({
    queryKey: ['labOrder', id, lineItemId],
    queryFn: () => httpClient.get(`lab_orders/${id}/retrieve_full_order`),
    enabled: !!id,
    staleTime: Infinity,
    refetchOnMount: 'always',
    onSuccess: (response) => {
      setCartCount(response.included.length)
    
      if (lineItemId) {
        const matchingOption = response?.included?.find(
          (obj) => obj.attributes.guid === lineItemId
        )
    
        if (!matchingOption) return
  
        if (matchingOption.type === 'album_order_detail') {
          const hydratedAlbum = hydrateAlbumFromLineItem(matchingOption.attributes)
          setProductType('albums')
          setLineItem(matchingOption)
          setPhotos(hydratedAlbum.photos)
          setSubtotal(hydratedAlbum.subtotal)

          if (hydratedAlbum.notes) {
            setNotes((prev) => ({
              ...prev,
              _all: hydratedAlbum.notes,
            }))
          }
          return
        }

        if (matchingOption.type === 'multi_openings_line_item') {
          setProductType('multi-opening')
          setLineItem(matchingOption)

          const rawSubtotal = parseFloat(matchingOption.attributes?.subtotal ?? matchingOption.attributes?.itemTotal)
          setSubtotal(isNaN(rawSubtotal) ? '0.00' : rawSubtotal.toFixed(2))

          if (matchingOption.attributes?.notes) {
            setNotes((prev) => ({ ...prev, _all: matchingOption.attributes.notes }))
          }

          useMultiOpeningMatStore.getState().hydrateFromLineItem(matchingOption.attributes)
          return
        }

        // existing regular line item logic — unchanged
        setPhotographGuid(matchingOption.attributes?.photographGuid)
        const rawSubtotal = parseFloat(matchingOption.attributes?.subtotal)
        setSubtotal(isNaN(rawSubtotal) ? '0.00' : rawSubtotal.toFixed(2))
        setIsEditInitializing(true)

        const finishingCategory = matchingOption.attributes?.finishingService?.finishingCategory?.toLowerCase() || ''
        const paperName = matchingOption.attributes?.printSizeAttributes?.paper?.toLowerCase() || ''
        const photographType = matchingOption.attributes?.photographType?.toLowerCase() || ''
        if (finishingCategory.includes('circle') || finishingCategory.includes('oval')) {
          setProductType('circle-oval')
        } else if (paperName.includes('canvas')) {
          setProductType('canvas')
        } else if (finishingCategory.includes('frame') || finishingCategory.includes('fram')) {
          setProductType('framed-prints')
        } else if (finishingCategory.includes('mount')) {
          setProductType('mounted-prints')
        } else if (finishingCategory.includes('matting') || finishingCategory.includes('mat')) {
          setProductType('matted-prints')
        } else if (photographType.includes('artist-proofs')) {
          setProductType('artist-proofs')
        } else if (photographType.includes('test-prints')) {
          setProductType('test-prints')
        } else {
          setProductType('prints')
        }
        setLineItem(matchingOption)

        if (matchingOption.attributes?.notes) {
          const photoGuid = matchingOption.attributes?.photographGuid
          if (photoGuid) {
            setNotes((prev) => ({
              _all: prev._all || '',
              ...prev,
              [photoGuid]: matchingOption.attributes.notes,
            }))
          }
        }
      }
    },
  })

  // Fetch paper types query
  const paperTypesQuery = useQuery({
    queryKey: ['paperTypes'],
    queryFn: () => httpClient.get('products/papers'),
    staleTime: Infinity,
    enabled: !lineItemId || (!!lineItemId && !!lineItem),
    onSuccess: (response) => {
      processPaperTypeData(response)
    },  
  })

  // Handle cached paper types data when lineItem changes
  useEffect(() => {
    if (lineItemId && lineItem && paperTypesQuery.data && !paperTypesQuery.isLoading) {
      processPaperTypeData(paperTypesQuery.data)
    }
  }, [lineItemId, lineItem, paperTypesQuery.data, paperTypesQuery.isLoading])

  // Set quantity from line item when editing
  useEffect(() => { 
    if (lineItemId && lineItem && quantityRef.current && typeof quantityRef.current === 'object') {
      const quantity = lineItem.attributes?.quantity || 1
      quantityRef.current.value = quantity
    }
  }, [lineItemId, lineItem])

  // Update photos with cart icons when both photos and lab order are loaded
  useEffect(() => {
    if (photos.length > 0 && labOrderQuery.data) {
      const { imagesInCart } = labOrderQuery.data.data.attributes
      const photosWithCartIcon = photos.map((photo) => {
        if (imagesInCart.includes(photo.attributes.guid)) {
          return {
            ...photo,
            attributes: {
              ...photo.attributes,
              inCart: true,
              isAddedToCart: true,
            },
          }
        }
        return photo
      })
      // Only update if there are changes
      const hasChanges = photosWithCartIcon.some((photo, index) => 
        photo.attributes.inCart !== photos[index]?.attributes?.inCart || photo.attributes.isAddedToCart !== photos[index]?.attributes?.isAddedToCart
      )
     if (hasChanges) {
        setPhotos(photosWithCartIcon)
      }
    }
  }, [photos, labOrderQuery.data, setPhotos])

  // reset edit mode when component unmounts
  useEffect(() => {
    return () => {
      if (lineItemId) {
        resetEditMode()
      }
    }
  }, [lineItemId, resetEditMode, setProductType, resetAlbumForm])

  return (
    <>
      <SuccessModal
        isOpen={isSuccessModalOpen}
        message={successModalMessage}
        onClose={() => {
          // Keep Shopping: stay on the current product page so the user can add
          setIsSuccessModalOpen(false);
        }}
        onCheckout={() => {
          setProductType('default');
          setIsSuccessModalOpen(false);
          navigate(`/lab-orders/${id}/summary`);
        }}
      />

      {productType === 'default' ? (
        lineItemId ? (
          <Loading message="Loading line item…" />
        ) : (
          <ProductTypeSelector />
        )
      ) : (
        <div className="w-layout-grid grid-print-order">
          <Modal isOpen={ModalIsOpen} onClose={() => setModalIsOpen(false)}>
            <h5>app.musealab.com</h5>
            <p>{reviewOrderModalMessage}</p>
          </Modal>

      <ProductSelectionSidebar
        orderId={id}
        lineItemId={lineItemId}
        quantityRef={quantityRef}
        queryClient={queryClient}
      />
  
      { productType === 'albums' && isComingSoonAlbum ? (
        <div className={`product-selection-picture-wrapper ${styles.comingSoonWrapper}`}>
          <div className={styles.comingSoonCentered}>
            <div className={styles.comingSoonContent}>
              <span>Coming Soon<span className={styles.comingSoonDots}>...</span></span>
              <a
                href={selectedAlbumType === 'museum-case' ? MUSEUM_CASE_ORDER_HERE_URL : DOUBLE_FOLIO_ORDER_HERE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.orderHereButton}
              >
                Order Here
              </a>
            </div>
          </div>
        </div>
      ) : productType === 'albums' ? (
        <div id="show-albums" className="product-selection-picture-wrapper">
          {addToCart ? (
            <Loading
              message={
                lineItemId ? 'Updating order…' : 'Adding album to cart…'
              }
            />
          ) : photosLoadingMessage ? (
            <Loading message={photosLoadingMessage} />
              ) : (selectedAlbumType && selectedAlbumType !== 'default') && albumItems.length === 0 && (uploadsPreview || []).filter(p => p.productType === 'albums').length === 0 ? (
            <div className={styles.albumUploadCentered}>
              <ImagesUpload
                disabled={false}
                onUpload={(files) => handleUploadPhoto(files, id, queryClient)}
                productType={productType}
              />
            </div>
          ) : albumItems.length > 0 || (uploadsPreview || []).filter(p => p.productType === 'albums').length > 0 ? (
            <div id="show-album" className={`photo-album-container grid show ${styles.albumGridContainer} ${styles.albumGridFullScroll}`}>
              <AlbumStatusBar />
              <AlbumGrid />
            </div>
              ) : (
                <PhotoDisplay
                  lineItemId={lineItemId}
                  isLoading={
                    lineItemId
                      ? (labOrderQuery.isLoading || (productType !== 'albums' && orderPhotoQuery.isLoading))
                      : photosQuery.isLoading
                  }
                />
              )}
        </div>
          ) : (
             <PhotoDisplay
              lineItemId={lineItemId}
              isLoading={
                lineItemId
                  ? (labOrderQuery.isLoading || (productType !== 'multi-opening' && orderPhotoQuery.isLoading))
                  : photosQuery.isLoading
              }
            />
          )}
        </div>
      )}
    </>
  )
}

export default ProductSelection
